import { Reducer } from "redux";
import { ResourceLoadingState, DeleteResourceState, SelectResourceState, AddEditResourceState, CachedFiltersState, SendEmailState } from "../../common/store/stateHelpers";
import { TruckModel, AddNewTruckModel } from "../TruckModels";
import { FilterInfo, RouteModel, EditMode } from "../../common/models/CommonModels";

export enum ActionType {
    AddNew = "TRUCKS/ADD_NEW",
    AddNewStarted = "TRUCKS/ADD_NEW_STARTED",
    AddNewSuccess = "TRUCKS/ADD_NEW_SUCCESS",
    AddNewCancelled = "TRUCKS/ADD_NEW_CANCELLED",
    AddNewError = "TRUCKS/ADD_NEW_ERROR",
    FormSubmitted = "TRUCKS/FORM_SUBMITTED",
    FormCancelled = "TRUCKS/FORM_CANCELLED",
    RequestFreeTrucks = "TRUCKS/REQUEST_FREE_TRUCKS",
    ReceivedFreeTrucks = "TRUCKS/RECEIVED_FREE_TRUCKS",
    Delete = "TRUCKS/DELETE",
    DeleteStarted = "TRUCKS/DELETE_STARTED",
    DeleteSubmitted = "TRUCKS/DELETE_SUBMIT",
    DeleteCancelled = "TRUCKS/DELETE_CANCEL",
    DeleteSuccess = "TRUCKS/DELETE_SUCCESS",
    DeleteError = "TRUCKS/DELETE_ERROR",
    TruckSelected = "TRUCKS/SELECT_TRUCK",
    RequestTruckRoutes = "TRUCKS/REQUEST_ROUTES",
    ReceivedTruckRoutes = "TRUCKS/RECEIVED_ROUTES",
    CacheFilters = "TRUCKS/CACHE_FILTERS",
    Copy = "TRUCKS/COPY",
    CopyStarted = "TRUCKS/COPY_STARTED",
    CopySuccess = "TRUCKS/COPY_SUCCESS",
    SendEmail = "TRUCKS/SEND_EMAIL",
    SendEmailStarted = "TRUCKS/SEND_EMAIL_STARTED",
    SendEmailSubmitted = "TRUCKS/SEND_EMAIL_SUBMITTED",
    SendEmailCancelled = "TRUCKS/SEND_EMAIL_CANCELLED",
    SendEmailSuccess = "TRUCKS/SEND_EMAIL_SUCCESS",
    SendEmailError = "TRUCKS/SEND_EMAIL_ERROR",
    SendEmailToReporter = "TRUCKS/SEND_EMAIL_TO_REPORTER",
    SendEmailToReporterStarted = "TRUCKS/SEND_EMAIL_TO_REPORTER_STARTED",
    SendEmailToReporterSubmitted = "TRUCKS/SEND_EMAIL_TO_REPORTER_SUBMITTED",
    SendEmailToReporterSuccess = "TRUCKS/SEND_EMAIL_TO_REPORTER_SUCCESS",
    SendEmailToReporterCancelled = "TRUCKS/SEND_EMAIL_TO_REPORTER_CANCELLED",
    SendEmailToReporterError = "TRUCKS/SEND_EMAIL_TO_REPORTER_ERROR"
}

export interface AddOrEditTruckAction {
    type: ActionType.AddNew;
    truck?: AddNewTruckModel;
    afterAddOrEdit?: () => void;
    reloadTrucks?: boolean;
}

interface AddOrEditTruckStartedAction {
    type: ActionType.AddNewStarted;
    truck?: AddNewTruckModel;
};

interface AddOrEditTruckSuccessAction {
    type: ActionType.AddNewSuccess;
}

interface AddOrEditFormSubmittedAction {
    type: ActionType.FormSubmitted
    data: AddNewTruckModel
};

interface AddOrEditFormCancelledAction {
    type: ActionType.FormCancelled
};

interface AddNewCancelledAction {
    type: ActionType.AddNewCancelled;
}

export interface RequestFreeTrucksAction {
    type: ActionType.RequestFreeTrucks;
    filters: FilterInfo,
    includeRoutes?: boolean;
}

interface ReceivedFreeTrucksAction {
    type: ActionType.ReceivedFreeTrucks;
    data: TruckModel[]
}

export interface DeleteAction {
    type: ActionType.Delete,
    truck: TruckModel,
    afterDelete?: () => void;
}

interface DeleteStartedAction {
    type: ActionType.DeleteStarted,
    truck: TruckModel
}

interface DeleteSuccessAction {
    type: ActionType.DeleteSuccess
}

interface DeleteErrorAction {
    type: ActionType.DeleteError,
    error: any
}

interface DeleteSubmittedAction {
    type: ActionType.DeleteSubmitted,
    data: any
}

interface DeleteCancelledAction {
    type: ActionType.DeleteCancelled
}
interface SelectTruckAction {
    type: ActionType.TruckSelected,
    selected: TruckModel | null
}

interface RequestTruckRoutesAction {
    type: ActionType.RequestTruckRoutes;
}

interface ReceivedTruckRoutesAction {
    type: ActionType.ReceivedTruckRoutes;
    routes: RouteModel[]
}

interface CacheFiltersAction {
    type: ActionType.CacheFilters,
    filters: FilterInfo
}

export interface CopyAction {
    type: ActionType.Copy;
    truck: AddNewTruckModel;
}

interface CopyStartedAction {
    type: ActionType.CopyStarted;
    truck: AddNewTruckModel;
}

interface CopySuccessAction {
    type: ActionType.CopySuccess;
}

export interface SendEmailAction {
    type: ActionType.SendEmail;
    truck: TruckModel;
}

interface SendEmailStartedAction {
    type: ActionType.SendEmailStarted;
    truck: TruckModel;
}

interface SendEmailSubmittedAction {
    type: ActionType.SendEmailSubmitted;
    data: any;
}

interface SendEmailSuccessAction {
    type: ActionType.SendEmailSuccess;
}

interface SendEmailErrorAction {
    type: ActionType.SendEmailError;
}

interface SendEmailCancelledAction {
    type: ActionType.SendEmailCancelled;
}

export interface SendEmailToReporterAction {
    type: ActionType.SendEmailToReporter;
    truck: TruckModel;
}

interface SendEmailToReporterStartedAction {
    type: ActionType.SendEmailToReporterStarted;
    truck: TruckModel;
}

interface SendEmailToReporterSuccessAction {
    type: ActionType.SendEmailToReporterSuccess;
}

interface SendEmailToReporterCancelledAction {
    type: ActionType.SendEmailToReporterCancelled;
}

interface SendEmailToReporterSubmittedAction {
    type: ActionType.SendEmailToReporterSubmitted;
    data: any;
}

interface SendEmailToReporterErrorAction {
    type: ActionType.SendEmailToReporterError;
    data: any;
}


export type TruckAction = AddOrEditTruckStartedAction
    | AddOrEditTruckSuccessAction
    | AddNewCancelledAction
    | RequestFreeTrucksAction
    | ReceivedFreeTrucksAction
    | DeleteStartedAction
    | DeleteSuccessAction
    | RequestTruckRoutesAction
    | ReceivedTruckRoutesAction
    | SelectTruckAction
    | CacheFiltersAction
    | CopyStartedAction
    | CopySuccessAction
    | SendEmailStartedAction
    | SendEmailSuccessAction
    | SendEmailCancelledAction
    | SendEmailToReporterStartedAction
    | SendEmailToReporterSuccessAction
    | SendEmailToReporterCancelledAction;

export const actionCreators = {
    addOrEditTruck: (truck?: AddNewTruckModel, afterAddOrEdit?: () => void, reloadTrucks?: boolean) => <AddOrEditTruckAction>{ type: ActionType.AddNew, truck, afterAddOrEdit, reloadTrucks },
    addOrEditTruckStarted: (truck?: AddNewTruckModel) => <AddOrEditTruckStartedAction>{ type: ActionType.AddNewStarted, truck },
    addOrEditTruckSuccess: () => <AddOrEditTruckSuccessAction>{ type: ActionType.AddNewSuccess },
    addOrEditTruckCancelled: () => <AddNewCancelledAction>{ type: ActionType.AddNewCancelled },
    requestFreeTrucks: (filters: FilterInfo, includeRoutes?: boolean) => <RequestFreeTrucksAction>{ type: ActionType.RequestFreeTrucks, filters, includeRoutes },
    receivedFreeTrucks: (data: TruckModel[]) => <ReceivedFreeTrucksAction>{ type: ActionType.ReceivedFreeTrucks, data },
    deleteTruck: (truck: TruckModel, afterDelete?: () => void) => <DeleteAction>{ type: ActionType.Delete, truck, afterDelete },
    deleteStarted: (truck: TruckModel) => <DeleteStartedAction>{ type: ActionType.DeleteStarted, truck },
    deleteSuccess: () => <DeleteSuccessAction>{ type: ActionType.DeleteSuccess },
    deleteError: (error: any) => <DeleteErrorAction>{ type: ActionType.DeleteError, error },
    deleteSubmitted: (data: any) => <DeleteSubmittedAction>{ type: ActionType.DeleteSubmitted, data },
    deleteCancelled: () => <DeleteCancelledAction>{ type: ActionType.DeleteCancelled },
    addOrEditFormSubmitted: (inputData: AddNewTruckModel) => <AddOrEditFormSubmittedAction>{ type: ActionType.FormSubmitted, data: inputData },
    addOrEditFormCancelled: () => <AddOrEditFormCancelledAction>{ type: ActionType.FormCancelled },
    selectTruck: (truck: TruckModel | null) => <SelectTruckAction>{ type: ActionType.TruckSelected, selected: truck },
    requestTruckRoutes: () => <RequestTruckRoutesAction>{ type: ActionType.RequestTruckRoutes },
    receivedTruckRoutes: (routes: RouteModel[]) => <ReceivedTruckRoutesAction>{ type: ActionType.ReceivedTruckRoutes, routes },
    cacheFilters: (filters: FilterInfo) => <CacheFiltersAction>{ type: ActionType.CacheFilters, filters },
    copyTruck: (truck: AddNewTruckModel) => <CopyAction>{ type: ActionType.Copy, truck },
    copyStarted: (truck: AddNewTruckModel) => <CopyStartedAction>{ type: ActionType.CopyStarted, truck },
    copySuccess: () => <CopySuccessAction>{ type: ActionType.CopySuccess },
    sendEmail: (truck: TruckModel) => <SendEmailAction>{ type: ActionType.SendEmail, truck },
    sendEmailStarted: (truck: TruckModel) => <SendEmailStartedAction>{ type: ActionType.SendEmailStarted, truck },
    sendEmailSubmitted: (data: any) => <SendEmailSubmittedAction>{ type: ActionType.SendEmailSubmitted, data },
    sendEmailSuccess: () => <SendEmailSuccessAction>{ type: ActionType.SendEmailSuccess },
    sendEmailError: () => <SendEmailErrorAction>{ type: ActionType.SendEmailError },
    sendEmailCancelled: () => <SendEmailCancelledAction>{ type: ActionType.SendEmailCancelled },
    sendEmailToReporter: (truck: TruckModel) => <SendEmailToReporterAction>{ type: ActionType.SendEmailToReporter, truck },
    sendEmailToReporterStarted: (truck: TruckModel) => <SendEmailToReporterStartedAction>{ type: ActionType.SendEmailToReporterStarted, truck },
    sendEmailToReporterSubmitted: (data: any) => <SendEmailToReporterSubmittedAction>{ type: ActionType.SendEmailToReporterSubmitted, data },
    sendEmailToReporterSuccess: () => <SendEmailToReporterSuccessAction>{ type: ActionType.SendEmailToReporterSuccess },
    sendEmailToReporterCancelled: () => <SendEmailToReporterCancelledAction>{ type: ActionType.SendEmailToReporterCancelled },
    sendEmailToReporterError: () => <SendEmailToReporterErrorAction>{ type: ActionType.SendEmailToReporterError }
}

interface TruckRoutes {
    routes: RouteModel[];
}

export type TrucksState = ResourceLoadingState<TruckModel> &
    AddEditResourceState<AddNewTruckModel> &
    SelectResourceState<TruckModel> &
    DeleteResourceState<TruckModel> &
    TruckRoutes &
    CachedFiltersState &
    EditMode &
    SendEmailState<TruckModel>;

const initialState: TrucksState = {
    isLoading: false,
    data: [],
    isEditing: false,
    editedItem: undefined,
    isDeleting: false,
    selected: null,
    routes: [],
    filters: undefined,
    editMode: undefined,
    emailItem: null,
    isSendingEmail: false,
    isSendingEmailToReporter: false
}

export const reducer: Reducer<TrucksState> = (state: TrucksState, action: TruckAction) => {
    switch (action.type) {
        case ActionType.RequestFreeTrucks:
            return {
                ...state,
                isLoading: true,
                data: []
            }
        case ActionType.ReceivedFreeTrucks:
            return {
                ...state,
                isLoading: false,
                data: action.data
            }
        case ActionType.AddNewStarted:
            return {
                ...state,
                isEditing: true,
                editedItem: action.truck,
                editMode: action.truck ? "edit" : "add"
            }
        case ActionType.AddNewSuccess:
        case ActionType.AddNewCancelled:
            return {
                ...state,
                isEditing: false,
                editedItem: undefined,
                editMode: undefined
            }
        case ActionType.DeleteStarted:
            return {
                ...state,
                isDeleting: true,
                toDeleteItem: action.truck
            }
        case ActionType.DeleteSuccess:
            return {
                ...state,
                isDeleting: false,
                toDeleteItem: null
            }
        case ActionType.TruckSelected:
            return {
                ...state,
                selected: action.selected,
            }
        case ActionType.RequestTruckRoutes:
            return {
                ...state,
                routes: []
            }
        case ActionType.ReceivedTruckRoutes:
            const newRoutes = [...state.routes, ...action.routes];
            return {
                ...state,
                routes: newRoutes
            }
        case ActionType.CacheFilters:
            return {
                ...state,
                filters: action.filters
            }
        case ActionType.CopyStarted:
            return {
                ...state,
                isEditing: true,
                editedItem: action.truck,
                editMode: "copy"
            }
        case ActionType.CopySuccess:
            return {
                ...state,
                isEditing: false,
                editedItem: undefined,
                editMode: undefined
            }
        case ActionType.SendEmailStarted:
            return {
                ...state,
                isSendingEmail: true,
                emailItem: action.truck
            }
        case ActionType.SendEmailSuccess:
            return {
                ...state,
                emailItem: null,
                isSendingEmail: false
            }
        case ActionType.SendEmailCancelled:
            return {
                ...state,
                emailItem: null,
                isSendingEmail: false
            }
        case ActionType.SendEmailToReporterStarted:
            return {
                ...state,
                isSendingEmailToReporter: true,
                emailItem: action.truck
            }
        case ActionType.SendEmailToReporterSuccess:
            return {
                ...state,
                emailItem: null,
                isSendingEmailToReporter: false
            }
        case ActionType.SendEmailToReporterCancelled:
            return {
                ...state,
                emailItem: null,
                isSendingEmailToReporter: false
            }
        default:
            const exhaustiveCheck: never = action;
    }

    return state || initialState;
}


// WEBPACK FOOTER //
// ./src/trucks/store/truckStore.ts